// ./src/utils/api/SignupItemService.ts
//
// Signup-list item (potluck dish / volunteer shift / merch / donation…) service.
// OpResult-native, event-scoped: every list is filtered by eventId. Full single-item
// CRUD plus a bulk status change / remove (array of ids in the where clause). Talks to
// the eventSignupItem* GraphQL operations. Item photos are NOT part of this record —
// they live in ms_storage under the context path e/{eventId}/su/{id} (main = 'cover',
// extras = 'gallery'). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

const SIGNUP_ITEM_FIELDS = `
  id
  eventId
  accountId
  categoryId
  name
  description
  link
  price
  currencyCode
  priceUnit
  quantityAvailable
  maxQuantityPerGuest
  addedByGuestId
  addedByGuestName
  images { uploadedFileId url uploadedFile { id fileUrl thumbnailUrl } }
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const SIGNUP_ITEM_LIST_QUERY = gql`
  query EventSignupItemList($filter: EventSignupItemFilter, $params: PaginationAndSorting) {
    eventSignupItemList(filter: $filter, params: $params) {
      code
      data { ${SIGNUP_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SIGNUP_ITEM_GET_QUERY = gql`
  query EventSignupItemGet($id: ID) {
    eventSignupItemGet(id: $id) {
      code
      data { ${SIGNUP_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SIGNUP_ITEM_GET_MANY_QUERY = gql`
  query EventSignupItemGetMany($ids: [ID]) {
    eventSignupItemGetMany(ids: $ids) {
      code
      data { ${SIGNUP_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SIGNUP_ITEM_CREATE_MUTATION = gql`
  mutation EventSignupItemCreate($params: EventSignupItemInput) {
    eventSignupItemCreate(params: $params) {
      code
      data { ${SIGNUP_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SIGNUP_ITEM_UPDATE_MUTATION = gql`
  mutation EventSignupItemUpdate($where: EventSignupItemWhereInput, $params: EventSignupItemInput) {
    eventSignupItemUpdate(where: $where, params: $params) {
      code
      data { ${SIGNUP_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SIGNUP_ITEM_REMOVE_MUTATION = gql`
  mutation EventSignupItemRemove($where: EventSignupItemWhereInput) {
    eventSignupItemRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventSignupItem schema)
// =============================================================================

/** A resolvable file ref (from ms_storage) — freshest URL for a stored uploaded_file. */
export interface SignupItemFileRef {
  id?: string | null;
  fileUrl?: string | null;
  thumbnailUrl?: string | null;
}

/** One image on a signup item — an uploaded_file ref (upload/library) and/or a URL (stock/URL). */
export interface SignupItemImage {
  uploadedFileId?: string | null;
  url?: string | null;
  /** Resolved file (federation) — present only for uploadedFileId images once the gateway is recomposed. */
  uploadedFile?: SignupItemFileRef | null;
}

export interface SignupItem {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  /** Link to an external website (product page / details / signup form). */
  link?: string | null;
  price?: number | null;
  currencyCode?: string | null;
  priceUnit?: string | null;
  /** Total quantity offered; null = unlimited. */
  quantityAvailable?: number | null;
  /** Max quantity one guest may claim in total; null = no cap. */
  maxQuantityPerGuest?: number | null;
  /** #246 — the guest who added it on the event website; null = the host. */
  addedByGuestId?: string | null;
  addedByGuestName?: string | null;
  /** Flat photo gallery (first entry = main). */
  images?: SignupItemImage[] | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
  /** CLIENT-ONLY: how many guest parties upvoted this item, merged in from UpvoteCountService
   *  (not from the list query). The list shows it as a badge. */
  upvoteCount?: number;
}

/** A single image's display URL: freshest resolved file URL, else the stored URL. */
export const signupItemImageUrl = (img?: SignupItemImage | null): string | null =>
  img?.uploadedFile?.fileUrl || img?.url || null;

/** The main (first) image URL to display, or null when the item has no photos. */
export const signupItemMainImageUrl = (item?: SignupItem | null): string | null =>
  signupItemImageUrl(item?.images?.[0]);

export interface SignupItemImageInput {
  uploadedFileId?: string | null;
  url?: string | null;
}

export interface SignupItemInput {
  eventId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  link?: string | null;
  price?: number | null;
  priceUnit?: string | null;
  quantityAvailable?: number | null;
  maxQuantityPerGuest?: number | null;
  /** Flat photo gallery (first entry = main). */
  images?: SignupItemImageInput[] | null;
  orderNo?: number | null;
  status?: number | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface SignupItemBulkInput {
  status?: number | null;
  /** Reassign every selected item's category, or null to clear it. */
  categoryId?: string | null;
}

export interface SignupItemFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  categoryId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface SignupItemWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class SignupItemService {
  static async list(
    filter?: SignupItemFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(SIGNUP_ITEM_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more
   * button, so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: SignupItemFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<SignupItem>(SignupItemService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(SIGNUP_ITEM_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(SIGNUP_ITEM_GET_MANY_QUERY, { ids });
  }

  static async create(params: SignupItemInput): Promise<OpResult> {
    return ApiService.executeMutation(SIGNUP_ITEM_CREATE_MUTATION, { params });
  }

  static async update(where: SignupItemWhereInput, params: SignupItemInput): Promise<OpResult> {
    return ApiService.executeMutation(SIGNUP_ITEM_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(SIGNUP_ITEM_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status on every selected item in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: SignupItemBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(SIGNUP_ITEM_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected item in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(SIGNUP_ITEM_REMOVE_MUTATION, { where: { id: ids } });
  }
}
